import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, toast } from '@gitbook/spine-react';

import { useUpgradeDialogContext } from '../../Components/UpgradeDialog';
import { useNavigation } from '../../navigation';
import { apiClient, apiQuery } from '../api';

type SiteCheckoutState = {
    buttonProps: Pick<ButtonProps, 'label' | 'leadingIcon' | 'trailingIcon' | 'kind' | 'href'>;
    onPress: () => Promise<api.Site | null>;
    state: api.SitePublishOperationPreviewResponse;
};

type SiteCheckoutOptions = {
    onUpgradeSuccess?: () => void;
};

/**
 * Returns the checkout state of a site.
 */
export function useCheckoutSite(
    organization: api.Organization,
    site: api.Site,
    options?: SiteCheckoutOptions
): SiteCheckoutState {
    const { navigate } = useNavigation();
    const { openUpgradeDialog } = useUpgradeDialogContext();
    const { onUpgradeSuccess } = options ?? {};
    const isFrozen = site.type === api.SiteType.Basic && site.appliedType !== site.type;
    const { data: checkout } = useSuspenseQuery(
        apiQuery('previewSitePublishCheckout', { organizationId: organization.id, plan: site.type })
    );

    return React.useMemo(() => {
        // The organization is in failed payment state. Send users to the billing page.
        if (organization.billing?.hasPaymentFailed) {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Check billing',
                    kind: 'danger',
                },
                onPress: async () => {
                    navigate({
                        overlay: {
                            type: 'org_settings',
                            organization: organization.id,
                            tab: { type: 'plans' },
                        },
                    });
                    return null;
                },
            };
        }

        if (checkout.type === 'go-ahead') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & pay',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            const { data: publishResult } = await apiClient.sites.publishSite(
                                organization.id,
                                site.id
                            );
                            // For go-ahead, the result should always be a Site
                            return publishResult as api.Site;
                        }, 'Error while publishing site')) ?? null
                    );
                },
            };
        }

        if (checkout.type === 'trial-available' || checkout.type === 'extended-trial-available') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Publish & start trial',
                    kind: 'upgrade',
                },
                onPress: async () => {
                    return (
                        (await toast.catch(async () => {
                            const { data: publishResult } = await apiClient.sites.publishSite(
                                organization.id,
                                site.id
                            );
                            // For trial-available, the result should always be a Site
                            return publishResult as api.Site;
                        }, 'Error while publishing site')) ?? null
                    );
                },
            };
        }

        // At this point, an upgrade is required.
        if (checkout.type === 'checkout') {
            return {
                state: checkout,
                buttonProps: {
                    label: 'Upgrade',
                    kind: 'upgrade',
                    leadingIcon: Icon.Upgrade,
                },
                onPress: async () => {
                    openUpgradeDialog({
                        organization,
                        targetProduct: api.BillingProduct.Pro2024,
                        reason: 'checkout_site',
                        initialState: {
                            publishedSites: new Map([[site.id, true]]),
                        },
                        onSuccess: () => {
                            onUpgradeSuccess?.();
                        },
                        analyticsContext: {
                            siteId: site.id,
                            siteType: site.type,
                            siteAppliedType: site.appliedType,
                            isFrozen,
                        },
                    });
                    return null;
                },
            };
        }

        throw new Error('unknown checkout type');
    }, [
        checkout,
        organization,
        site.id,
        site.type,
        site.appliedType,
        isFrozen,
        openUpgradeDialog,
        onUpgradeSuccess,
        navigate,
    ]);
}
